import { ReactElement, useMemo, type FC } from 'react';

import { useCashierCallbackConnected } from '@pp/core/src/hooks/useCashierCallbackConnected';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { useChatSlice } from '@pp/core/src/store/slices/chat/selectors';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { useHeaderSlice } from '@pp/core/src/store/slices/header/selectors';
import { isGameChatRestricted } from '@pp/core/src/utils/chat';

import { ChatFooter } from './ChatFooter';

export interface ChatFooterConnectedProps {
  isGameChat?: boolean;
}

export const ChatFooterConnected: FC<ChatFooterConnectedProps> = ({ isGameChat = false }): ReactElement => {
  const openedModal = useHeaderSlice.getCoreOpenedModal();
  const userId = useConfigSlice.playerConfig()?.userId ?? '';
  const casinoId = useConfigSlice.session()?.casinoId ?? '';
  const tableId = useConfigSlice.session()?.tableId ?? '';
  const dealerName = useGameStateSlice.dealer()?.name ?? '';
  const isGameChatBlocked = useChatSlice.getIsGameChatBlocked();
  const isLiveSupportChatBlocked = useChatSlice.getIsLiveSupportChatBlocked();
  const isBetChatAllowed = useChatSlice.getIsBetChatAllowed();
  const isBetRequiredToChat = useChatSlice.isBetRequiredToChat();
  const isLowBalance = useBetSlice.isLowBalance();
  const hasTotalBet = useBetSlice.hasTotalBet();

  const cashierCallback = useCashierCallbackConnected();

  const isGameChatUnavailable = useMemo(
    () => isGameChatRestricted(isBetRequiredToChat, isBetChatAllowed, isLowBalance, hasTotalBet),
    [hasTotalBet, isBetChatAllowed, isBetRequiredToChat, isLowBalance]
  );

  return (
    <ChatFooter
      cashierCallback={cashierCallback}
      casinoId={casinoId}
      dealerName={dealerName}
      isBetRequiredToChat={isBetRequiredToChat}
      isGameChat={isGameChat}
      isGameChatBlocked={isGameChatBlocked}
      isGameChatUnavailable={isGameChatUnavailable}
      isLiveSupportChatBlocked={isLiveSupportChatBlocked}
      isMiniChat={openedModal === null}
      tableId={tableId}
      userId={userId}
    />
  );
};
